export function getScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

export function getEffectiveZoom(el: Element | null): number {
  let zoom = 1;
  for (let node: Element | null = el; node; node = node.parentElement) {
    /**
     * Prefer explicit data-zoom (covers both CSS `zoom` and `transform: scale`,
     * and is reliable on iOS Safari where computed style may not reflect actual values).
     */
    const dataZoom = (node as HTMLElement).dataset?.zoom;
    if (dataZoom) {
      const parsedData = parseFloat(dataZoom);
      if (!isNaN(parsedData) && parsedData > 0) {
        zoom *= parsedData;
        continue;
      }
    }
    const raw = getComputedStyle(node).zoom;
    if (!raw) continue;
    const parsed = raw.endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw);
    if (!isNaN(parsed) && parsed > 0) zoom *= parsed;
  }
  return zoom;
}

/**
 * Set of ID creators so we can keep track of any IDs we strap to DOM elements and make sure there
 * are no global conflicts.
 */
export const domElIdCreators = {
  /**
   * Deliberately not the `#activity:<id>` URL fragment an activity link carries. An element whose
   * id matches the fragment makes the browser scroll the row itself — flush to the top of the
   * scrollport — competing with (and beating) the feed's virtualized centering.
   */
  activityRowEl: (activityItemId: UUID) => `activity-row:${activityItemId}`,
  advanceWorkflowButton: () => 'advance-workflow-button',
  alineSearchInputEl: () => 'aline-search-input',
  commentEl: (commentId: UUID) => `comment:${commentId}`,
  docCommentBoxEl: (nodeId: UUID) => `doc-comment-box:${nodeId}`,
  documentSummaryRelatedDocumentsHeader: (documentId: UUID) =>
    `document-summary-related-documents-header:${documentId}`,
  fieldOverlayEl: () => 'field-drag-overlay',
  pageEl: (tabId: UUID, pageNumber: number) => `${tabId}-page-${pageNumber}`,
  questionEl: (questionId: UUID) => `question:${questionId}`,
  questionTypeMenuEl: () => 'question-type-menu',
  variableListBoxEl: (id: UUID) => `variable-row-listBox:${id}`,
  variableRowEl: (nodeId: UUID) => `variable-row:${nodeId}`,
} as const;
